// Ringtone chooser for the admin console: the list plus a play/stop button.
//
// Picking a tone previews it — you shouldn't have to save an org-wide setting to
// find out what it sounds like — and the button replays or interrupts it. One
// controller drives both, so the button always shows what is actually sounding.

import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { previewRingtone, RINGTONES } from '../sounds';
import { Select } from '../ui/Select';
import { IconPlay, IconStop } from '../ui/icons';

const OPTIONS = RINGTONES.map((r) => ({ value: r.id, label: r.label }));

export const RingtonePicker = memo(function RingtonePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const [playing, setPlaying] = useState(false);
  const stopRef = useRef<(() => void) | null>(null);

  const play = useCallback((id: string) => {
    setPlaying(true);
    stopRef.current = previewRingtone(id, () => setPlaying(false));
  }, []);

  const pick = useCallback(
    (id: string) => {
      onChange(id);
      play(id);
    },
    [onChange, play],
  );

  const toggle = useCallback(() => {
    if (playing) stopRef.current?.();
    else play(value);
  }, [playing, play, value]);

  // A preview must never outlive the panel that started it.
  useEffect(() => () => stopRef.current?.(), []);

  return (
    <div className="bz-ringpick">
      <Select
        value={value}
        options={OPTIONS}
        onChange={pick}
        label="Incoming-call ringtone"
        className="bz-ringpick-select"
        popoverClassName="bz-console-theme"
      />
      <button
        type="button"
        className={playing ? 'bz-ringpick-play bz-ringpick-play-on' : 'bz-ringpick-play'}
        onClick={toggle}
        aria-label={playing ? 'Stop preview' : 'Play preview'}
      >
        {playing ? <IconStop size={16} /> : <IconPlay size={16} />}
      </button>
    </div>
  );
});
